import { createPortal } from 'react-dom';

// mui
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import JxForm from '../../../JxControls/JxForms/JxForm';
import JxTitleBar from '../../../JxControls/JxTitleBar/JxTitleBar';
import componentNotifier from '../../../services/wss/ComponentNotifier';
import uploadAttachmentHandler from './UploadAttachmentHandler';
import UploadDropzone from './UploadDropzone';
import UploadFileList from './UploadFileList';
import UploadFilePanel from './UploadFilePanel';
import UploadProgress from './UploadProgress';

import '../../../JxControls/JxTheme.css';
import './UploadAttachmentDialog.css';

/**
 * Pasted images always come in from the browser with the same generic name
 * (e.g. "image.png") — a 2nd paste would overwrite the first one on upload.
 * Gives the file a unique name with a UTC timestamp (ms precision). Drag/drop and
 * the file picker already have a real, unique file name and never go through this.
 * @param {File} file - pasted file from clipboardData
 * @returns {File} new File with a unique name, same content/type
 */
const renamePastedFile = (file) => {
    const ext = file.type.split('/')[1] || 'png';
    const stamp = new Date().toISOString().replace(/[:.]/g, '-'); // e.g. 2026-07-06T12-34-56-789Z
    return new File([file], `image-${stamp}.${ext}`, { type: file.type });
};

/**
 * Upload dialog — floating JxForm tool window. Draggable via the titlebar, closes via
 * the titlebar close action or Escape (JxForm handles Escape automatically).
 * Owns only isDragging (unused for now, kept for UploadDropzone's visual feedback prop)
 * — all upload state lives in uploadAttachmentHandler, repaint on every notify.
 * Layout switches between single-file (full width) and multi-file (left + right columns).
 * Locked during upload — dropzone, panel, list and clear button all disabled.
 * @param {string} jid - the entry rk
 * @param {boolean} open - controls visibility
 * @param {Function} onClose - called on close
 * @param {string[]} providers - available storage provider keys
 */
export default class UploadAttachmentDialog extends JxForm {
    constructor(props) {
        super(props);
        this.name = 'UploadAttachmentDialog';
        this.schema = null;

        this.state = {
            ...this.state,
            isDragging: false,
        };
    }

    // Literal default geo (JxFormBase contract), width/height come from CSS (.upload-dlg-card)
    getDefaultGeo() {
        return { top: 120, left: 780, width: 360, height: 400 };
    }

    componentDidMount() {
        super.componentDidMount();
        this._unsubscribeUpload = componentNotifier.subscribe('uploadStateChanged', this._handleUploadStateChanged);
    }

    componentWillUnmount() {
        if (this._unsubscribeUpload) this._unsubscribeUpload();
        super.componentWillUnmount();
    }

    componentDidUpdate(prevProps) {
        super.componentDidUpdate(prevProps); // REQUIRED — otherwise no bringToTop, focus or toolbar color
    }

    /**
     * Repaint on every uploadAttachmentHandler notify — mirrors the old forceRepaint counter.
     */
    _handleUploadStateChanged = () => {
        this.forceUpdate();
    };

    _setIsDragging = (value) => this.setState({ isDragging: value });

    _getDefaultProvider = () => this.props.providers?.[0] ?? '';

    /**
     * Close action — no Save/Cancel distinction here, just closes.
     */
    handleClose = () => {
        try {
            this.props.onClose?.();
        } catch (error) {
            this.logError('handleClose', error.message);
        }
    };

    /**
     * Handles Ctrl+V paste — renames pasted images to a unique name, delegates to handler.
     * @param {ClipboardEvent} e
     */
    _handlePaste = (e) => {
        if (uploadAttachmentHandler.uploadState === 'uploading') return;
        const pastedFiles = Array.from(e.clipboardData?.files ?? []).map(renamePastedFile);
        uploadAttachmentHandler.dispatch('onFilesAdded', {
            fileList: pastedFiles,
            defaultProvider: this._getDefaultProvider(),
        });
    };

    /**
     * Triggers push all — async, handler updates progress state per file.
     */
    _handlePushAll = () => {
        const { files, uploadState } = uploadAttachmentHandler;
        if (!files.length || uploadState === 'uploading') return;
        uploadAttachmentHandler.dispatchPushAll();
    };

    /**
     * Clears all files and resets state.
     */
    _handleClear = () => {
        if (uploadAttachmentHandler.uploadState === 'uploading') return;
        uploadAttachmentHandler.dispatch('onClear', {});
    };

    getTitleBarActions() {
        return [
            { icon: <CloseIcon sx={{ fontSize: 16 }} />, title: 'Close', onClick: this.handleClose },
        ];
    }

    render() {
        if (!this.props.open) return null;

        const { jid, providers = [] } = this.props;
        const { isDragging } = this.state;
        const { files, selectedIndex, uploadState, uploadProgress } = uploadAttachmentHandler;
        const selectedItem = files[selectedIndex] ?? null;
        const isUploading = uploadState === 'uploading';
        const isMulti = files.length > 1;
        const defaultProvider = this._getDefaultProvider();

        uploadAttachmentHandler.setJid(jid);

        const panel = (
            <div
                {...this.panelProps}
                style={this.panelStyle}
                tabIndex={0}
                className={`upload-dlg-card ${isMulti ? 'multi' : ''} ${this.panelActiveClass}`}
                onPaste={this._handlePaste}
            >
                <JxTitleBar
                    id={`${this.name}_titlebar`}
                    title="Add attachment"
                    icon={<UploadFileIcon sx={{ fontSize: 14, color: 'var(--j-accent)' }} />}
                    variant="toolwindow"
                    isDraggable={true}
                    dragHandleProps={this.dragHandleProps}
                    actions={this.getTitleBarActions()}
                />

                {/* Body — single column or two columns depending on file count */}
                <div id="upload-dlg-body" className="upload-dlg-body">
                    <div id="upload-dlg-left" className="upload-dlg-left">
                        <UploadDropzone
                            selectedItem={selectedItem}
                            defaultProvider={defaultProvider}
                            locked={isUploading}
                            isDragging={isDragging}
                            onDragChange={this._setIsDragging}
                        />

                        {/* Panel only shown when at least one file is selected */}
                        {selectedItem && (
                            <UploadFilePanel item={selectedItem} index={selectedIndex} providers={providers} locked={isUploading} />
                        )}
                    </div>

                    {/* Thumbnail list — only rendered for multi-file selection */}
                    {isMulti && (
                        <div id="upload-dlg-right" className="upload-dlg-right">
                            <UploadFileList files={files} selectedIndex={selectedIndex} locked={isUploading} />
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div id="upload-dlg-footer" className="upload-dlg-footer">
                    <UploadProgress progress={uploadProgress} />

                    <div id="upload-dlg-actions" className="upload-dlg-actions">
                        <button
                            id="upload-dlg-clear-btn"
                            className="upload-dlg-clear-btn"
                            onClick={this._handleClear}
                            disabled={isUploading || !files.length}
                        >
                            Clear all
                        </button>

                        <button
                            id="upload-dlg-push-btn"
                            className={`upload-dlg-push-btn ${!files.length || isUploading ? 'disabled' : ''}`}
                            onClick={this._handlePushAll}
                            disabled={!files.length || isUploading}
                        >
                            {isMulti ? 'Push all to assigned storage' : 'Push to storage'}
                        </button>
                    </div>
                </div>
            </div>
        );

        return createPortal(panel, document.body);
    }
}
